// material-ui
import {
  Box,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  AccessTime as ClockIcon,
  Circle as CircleIcon,
  Comment as CommentIcon,
} from "@mui/icons-material";
import { useUpdateMarkAsReadMutation } from "api/api-notifications";
import { getTimeStamp } from "helpers/getTimeStamp";
import history from "helpers/history";
import { useNotificationHandler } from "hooks/useNotificationHandler";
import { INCOMLEND_INTERNAL_LINK } from "codes";
import { PERMISSIONS } from "permissions";
import { useAppSelector } from "redux/hooks";

import { useTranslation } from "react-i18next";
import { Fragment } from "react/jsx-runtime";
import type { INotification } from "api/api-notifications.d";

// ==============================|| NOTIFICATION LIST ITEM ||============================== //

const categoryLabel: Record<string, string> = {
  lead_comment: "Comment",
  map_comment_mention: "Mention",
  new_billing: "Billing",
  invoice_comment: "Invoice",
  invoice_finance_assignment: "Invoice",
  withdrawal_deposit_created: "WD request",
  withdrawal_deposit_operation: "WD operation",
  withdrawal_deposit_completed: "WD completed",
  withdrawal_deposit_comment: "WD message",
  withdrawal_deposit_mention: "WD mention",
  book_meeting: "Book meeting",
};

const NotificationList = ({
  notifications,
  onNotificationClick: onCloseDropdown,
}: {
  notifications: INotification[];
  onNotificationClick?: () => void;
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [markAsRead] = useUpdateMarkAsReadMutation();
  const { handleNotificationClick } = useNotificationHandler();
  const userPermissions = useAppSelector(
    (state) => state.appState.user_permission,
  );
  const isOperationAdmin = userPermissions.includes(
    PERMISSIONS.map_operation_decision,
  );

  const handleItemClick = (notif: INotification, action: () => void) => {
    action();
    onCloseDropdown?.();
  };

  const getTimestamp = (send_at: Date) => {
    const [amount, unit] = getTimeStamp(send_at);
    if (unit === "now") return t("now");
    return t("notif-delai", { number: amount, unity: t(unit) });
  };

  const getMessage = (notif: INotification) => {
    if (notif.category === "invoice_finance_assignment") {
      const invoiceName = String(notif.context?.invoice_name ?? "").trim();
      const message = String(notif.message || notif.content || "").trim();

      if (!invoiceName) return message || "Invoice assigned to finance team";
      if (!message) return `${invoiceName} assigned to finance team`;

      const redundantPrefix = `Invoice ${invoiceName}`;
      return message.startsWith(redundantPrefix)
        ? `${invoiceName}${message.slice(redundantPrefix.length)}`
        : message.includes(invoiceName)
          ? message
          : `${invoiceName} - ${message}`;
    }

    if (notif.category === "invoice_comment" && isOperationAdmin) {
      const invoiceName = notif.context?.invoice_name;
      return invoiceName
        ? `${invoiceName} Pending for Second-Level Approval`
        : "Invoice Pending for Second-Level Approval";
    }

    return (
      (notif.message && String(notif.message)) ||
      (notif.content &&
        (notif.context
          ? t(notif.content, notif.context).toString()
          : String(notif.content))) ||
      (notif.category === "lead_comment" ||
      notif.category === "map_comment_mention" ||
      notif.category === "invoice_comment"
        ? "New comment"
        : notif.category === "new_billing"
          ? "Setup fees paid"
          : "New notification")
    );
  };

  const isCommentCategory = (cat: string) =>
    cat === "lead_comment" ||
    cat === "map_comment_mention" ||
    cat === "invoice_comment" ||
    cat === "withdrawal_deposit_comment" ||
    cat === "withdrawal_deposit_mention" ||
    cat.startsWith("withdrawal_deposit_");

  return (
    <Stack
      spacing={1}
      sx={{
        width: "100%",
        maxWidth: 360,
        [theme.breakpoints.down("md")]: { maxWidth: 320 },
      }}
    >
      {notifications?.map((notif) => (
        <Fragment key={notif?.id}>
          <Box
            onClick={() => {
              if (
                notif.category === "lead_comment" ||
                notif.category === "map_comment_mention" ||
                notif.category === "invoice_comment" ||
                notif.category === "invoice_finance_assignment" ||
                notif.category === "new_billing" ||
                notif.category === "book_meeting" ||
                notif.category?.startsWith("/incomlend/opportunity/") ||
                notif.category?.startsWith("withdrawal_deposit_")
              ) {
                handleItemClick(notif, () => handleNotificationClick(notif));
              } else {
                handleItemClick(notif, () => {
                  markAsRead(notif.id)
                    .unwrap()
                    .then(() => {
                      if (notif.category) {
                        const path = notif.category.startsWith("/")
                          ? notif.category
                          : `${INCOMLEND_INTERNAL_LINK}/${notif.category}`;
                        history.push(path);
                      }
                    })
                    .catch(() => {});
                });
              }
            }}
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              background: notif.notif_is_read
                ? theme.palette.background.paper
                : alpha(theme.palette.primary.main, 0.04),
              cursor: "pointer",
              transition: "box-shadow 0.15s",
              "&:hover": {
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.12)}`,
              },
            }}
          >
            {/* Card header: unread dot + title + comment icon */}
            <Stack
              direction="row"
              alignItems="center"
              sx={{ px: 2, pt: 1.25, pb: 0.5, gap: 1 }}
            >
              {!notif.notif_is_read && (
                <CircleIcon
                  sx={{ fontSize: 8, color: "error.main", flexShrink: 0 }}
                />
              )}
              <Typography
                variant="subtitle2"
                fontWeight={600}
                noWrap
                sx={{ flex: 1, color: theme.palette.text.primary }}
              >
                {notif.title || "Notification"}
              </Typography>

              {isCommentCategory(notif.category) && (
                <Tooltip title="View comments">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleItemClick(notif, () =>
                        handleNotificationClick(notif),
                      );
                    }}
                    sx={{
                      ml: 1,
                      flexShrink: 0,
                      width: 28,
                      height: 28,
                      background: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      borderRadius: 1,
                      "&:hover": {
                        background: theme.palette.primary.main,
                        color: "#fff",
                      },
                    }}
                  >
                    <CommentIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>

            {/* Card body: message */}
            <Box sx={{ px: 2, py: 1.25 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  lineHeight: 1.55,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {getMessage(notif)}
              </Typography>
            </Box>

            <Divider />

            {/* Card footer: category chip + timestamp */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 2, py: 0.75 }}
            >
              <Chip
                label={categoryLabel[notif.category] ?? notif.category}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  borderRadius: 1,
                  color: theme.palette.primary.main,
                  borderColor: alpha(theme.palette.primary.main, 0.4),
                }}
              />
              <Stack direction="row" spacing={0.5} alignItems="center">
                <ClockIcon sx={{ fontSize: 12, color: "text.disabled" }} />
                <Typography variant="caption" color="text.disabled">
                  {getTimestamp(notif.send_at)}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Fragment>
      ))}
    </Stack>
  );
};

export default NotificationList;
